import { computed, unref } from "vue"
import { AssessmentState } from "@/models/assessmentState"

export function getAssessmentLockMessage(lockedBy?: string | null): string {
	return `This assessment has been locked by ${lockedBy ?? "another user"}. It can only be unlocked by the person who locked it or an admin.`
}

export function useAssessmentLock(state: AssessmentState) {
	const isLocked = computed(() => state.isLocked)
	const message = computed(() => getAssessmentLockMessage(state.lockedBy))

	const input = computed(() => ({
		readOnly: isLocked.value,
	}))

	const select = computed(() => ({
		disabled: isLocked.value,
		pt: {
			root: {
				style: "pointer-events: auto; opacity: 1;",
			},
		},
		class: isLocked.value ? "locked-field" : undefined,
	}))

	const multiSelect = computed(() => ({
		disabled: isLocked.value,
		pt: {
			root: {
				style: "pointer-events:auto; opacity:1;",
			},
			labelContainer: {
				style: "pointer-events:auto;",
			},
			trigger: {
				style: "pointer-events:auto;",
			},
		},
		class: isLocked.value ? "locked-field" : undefined,
	}))

	const treeSelect = computed(() => ({
		disabled: isLocked.value,
		pt: {
			root: {
				style: "pointer-events: auto; opacity: 1;",
			},
		},
		class: isLocked.value ? "locked-field" : undefined,
	}))

	const button = computed(() => ({
		disabled: isLocked.value,
	}))

	const check = computed(() => ({
		disabled: isLocked.value,
	}))

	const radio = computed(() => ({
		disabled: isLocked.value,
	}))

	const icon = computed(() => ({
		class: isLocked.value ? "locked-icon" : undefined,
		tabindex: isLocked.value ? -1 : 0, // prevents focus
	}))

	const guard =
		<T extends (...args: any[]) => any>(fn: T) =>
		(...args: Parameters<T>) => {
			if (isLocked.value) return
			return fn(...args)
		}

	//Tooltip
	const tooltip = computed(() => (isLocked.value ? message.value : undefined))
	const toolTipTargetRoot = computed(() => (isLocked.value ? { value: message.value, getCustomTarget: (el: HTMLElement) => el } : undefined))

	return {
		isLocked,
		message,
		input,
		select,
		multiSelect,
		treeSelect,
		button,
		check,
		radio,
		icon,
		guard,
		tooltip,
		toolTipTargetRoot,
	}
}
